import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import Notifications from '../components/Notifications';
import AddLot from '../components/AddLot';
import { api } from '../config/api';
import { clearAuthData, getToken, getRefreshToken } from '../utils/auth';

interface Lot {
  lotId: string;
  displayLotId: string;
  companyName: string;
  address: string;
  lotName: string;
  lotCapacity: number;
  hasEvCharging: boolean;
  lotStatus: string;
  currentOccupancy: number;
  createdOn: string;
  owner: {
    userId: string;
    name: string;
    email: string;
  };
}

interface Device {
  deviceId: string;
  deviceDisplayId: string;
  lotId: string;
  deviceType: string;
  isWifiRegistered: boolean;
  wifiNetworkName: string;
  wifiPassword: string;
  ipAddress: string;
  deviceStatus: string;
  lastActive: string;
  deviceTemp: number;
  createdOn: string;
  createdBy: string;
  modifiedOn: string;
  modifiedBy: string;
  isDeleted: boolean;
}

interface DeviceStatusCounts {
  online: number;
  calibrating: number;
  offline: number;
  total: number;
}

interface SortConfig {
  key: keyof Lot | null;
  direction: 'ascending' | 'descending';
}

interface CursorPageResponse<T> {
  content: T[];
  nextCursorId: string | null;
  nextCursorTimestamp: string | null;
  hasMore: boolean;
  totalElements: number;
}

interface GenericResponse<T> {
  success: boolean;
  data: T;
  message: string;
  status: number;
}

const DEFAULT_PAGE_SIZE = 40; // Default page size for lots data

const Dashboard: React.FC = () => {
  const [lots, setLots] = useState<Lot[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'lotId', direction: 'ascending' });
  const [isNotificationsVisible, setNotificationsVisible] = useState(false);
  const [showAddLotModal, setShowAddLotModal] = useState(false);
  const [showArchivedLots, setShowArchivedLots] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [nextCursorId, setNextCursorId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);

  // Add a ref for debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement | null>(null);

  const formatLotId = (lotId: string): string => {
    // Extract the last 8 characters from the lotId
    const last8 = lotId.slice(-8);
    // If we have exactly 8 characters, insert a dash in between the first and last 4 digits
    if (last8.length === 8) {
      return last8.slice(0, 4) + '-' + last8.slice(4);
    }
    // Otherwise return the original lotId
    return lotId;
  };

  // Format date strings consistently across the application
  // This matches the formatDate function in Customer.tsx to ensure dates
  // are displayed in the same format everywhere
  const formatDate = (isoDate: string): string => {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleString();
  };

  // Load more data when scrolling to the bottom - with debounce
  const loadMoreData = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      if (hasMore && !isLoading) {
        fetchLots(nextCursorId, false);
      }
    }, 300);
  }, [hasMore, isLoading, nextCursorId]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMoreData();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreData, hasMore, isLoading]);

  // Fetch lots from Spring Boot with pagination
  const fetchLots = async (cursor?: string | null, reset: boolean = true) => {
    if (reset) {
      setIsLoading(true);
      setLots([]);
      setNextCursorId(null);
      setHasMore(false);
    }

    try {
      const params = new URLSearchParams();
      if (cursor) {
        params.append('cursor', cursor);
      }
      params.append('limit', DEFAULT_PAGE_SIZE.toString());

      const response = await api.get(`/parkinglots/get-all-parking-lots-paginated?${params.toString()}`);
      const data: GenericResponse<CursorPageResponse<Lot>> = response.data;

      if (data.success && data.data) {
        const newLots: Lot[] = data.data.content.map((item: any) => ({
          lotId: item.lotId,
          displayLotId: item.displayLotId || item.lotId,
          companyName: item.companyName,
          address: item.address,
          lotName: item.lotName,
          lotCapacity: item.lotCapacity || 0,
          hasEvCharging: item.hasEvCharging || false,
          lotStatus: item.lotStatus || 'ACTIVE',
          currentOccupancy: item.currentOccupancy || 0,
          createdOn: item.createdOn || new Date().toISOString(),
          owner: {
            userId: item.owner?.userId || '',
            name: item.owner?.name || 'Unknown',
            email: item.owner?.email || ''
          }
        }));

        if (reset) {
          setLots(newLots);
        } else {
          setLots(prev => [...prev, ...newLots]);
        }

        setNextCursorId(data.data.nextCursorId);
        setHasMore(data.data.hasMore);
      }
    } catch (error) {
      console.error('Error fetching lots:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch devices from Spring Boot
  const fetchDevices = async () => {
    try {
      const response = await api.get('/devices/get-all-devices');
      const data = response.data;
      if (data.success && data.data) {
        setDevices(data.data);
      } else {
        console.error('Failed to fetch devices:', data.message);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  useEffect(() => {
    fetchLots();
    fetchDevices();
  }, []);

  const handleLotCreated = () => {
    setShowAddLotModal(false);
    fetchLots(null, true);
  };

  const calculateDeviceStatus = (lotID: string): DeviceStatusCounts => {
    const relevantDevices = devices.filter((device) => device.lotId === lotID);

    const onlineCount = relevantDevices.filter((d) => d.deviceStatus === 'Online').length;
    const calibratingCount = relevantDevices.filter((d) => d.deviceStatus === 'Calibrating').length;
    const offlineCount = relevantDevices.filter((d) =>
      d.deviceStatus === 'Offline' || d.deviceStatus === 'RecentlyAdded'
    ).length;
    return {
      online: onlineCount,
      calibrating: calibratingCount,
      offline: offlineCount,
      total: relevantDevices.length,
    };
  };

  const handleLogout = async () => {
    try {
      // Get the refresh token before clearing auth data
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // Call logout API endpoint to invalidate tokens on the server
        await api.post('/login/logout', { refreshToken });
        if (process.env.NODE_ENV === "development") {
          console.log('SuperadminFE: Logout API call successful');
        }
      } else {
        if (process.env.NODE_ENV === "development") {
          console.warn('SuperadminFE: No refresh token available for logout API call');
        }
      }
    } catch (error) {
      console.error('SuperadminFE: Logout API call failed:', error);
      // Continue with logout even if API call fails
    }

    // Clear local auth data
    clearAuthData();

    // Redirect to login page
    window.location.href = '/login';
  };

  const handleOpenLot = (lotID: string) => {
    navigate(`/lot/${lotID}/device-manager`);
  };

  const toggleNotifications = () => {
    setNotificationsVisible((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setNotificationsVisible(false);
      }
    };

    if (isNotificationsVisible) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isNotificationsVisible]);

  const normalizeString = (str: string): string => {
    return str.toLowerCase().replace(/[-_]/g, ' ');
  };

  const filteredLots = lots
    .filter((lot) => {
      const normalizedSearch = normalizeString(searchQuery);

      // Show archived and suspended lots when the checkbox is checked
      if (showArchivedLots) {
        return (lot.lotStatus.toLowerCase() === 'archived' || lot.lotStatus === 'Suspended') &&
          normalizeString(`${lot.displayLotId} ${lot.companyName} ${lot.lotName} ${lot.address} ${lot.createdOn}`).includes(normalizedSearch);
      }

      // Default behavior: only show active or paused lots
      return (
        (lot.lotStatus === 'Active' || lot.lotStatus === 'Paused') &&
        normalizeString(`${lot.displayLotId} ${lot.companyName} ${lot.lotName} ${lot.address} ${lot.createdOn}`).includes(normalizedSearch)
      );
    })
    .sort((a, b) => {
      if (sortConfig.key) {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (sortConfig.direction === 'ascending') {
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        } else {
          return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        }
      }
      return 0;
    });

  const handleSort = (key: keyof Lot) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo">
            <img src="/assets/LogotypeSuperAdminHorizontal.svg" alt="Logo" />
          </div>
          <div className="header-actions">
            <button className="docs-nav-btn" onClick={() => navigate('/docs')}>
              <img src="/assets/DocsIcon.svg" alt="Docs" />
              <span>Docs</span>
            </button>
            <button className="docs-nav-btn" onClick={() => navigate('/agents')}>
              <img src="/assets/DocsIcon.svg" alt="Agents" />
              <span>Agents</span>
            </button>
            <div className="icon-wrapper" onClick={toggleNotifications} ref={popupRef}>
              <img src="/assets/NotificationIcon.svg" alt="Notifications" className="icon" />
            </div>
            {/* Notifications component */}
            {isNotificationsVisible && <Notifications onClose={() => setNotificationsVisible(false)} />}
            <img src="/assets/MessageIcon.svg" alt="Messages" className="icon" />
            <button onClick={handleLogout} className="logout-btn">
              <img src="/assets/Logout.svg" alt="Logout" className="logout-icon" />
              <span className="logout-text">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div className="search-bar" style={{ flex: '4', display: 'flex', alignItems: 'center' }}>
            <img src="/assets/SearchBarIcon.svg" alt="Search" />
            <input
              type="text"
              placeholder="Search LotID, Company Name, Lot Name, Purchase Date or Location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
            />
            <div className="archive-checkbox">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="showArchived"
                  checked={showArchivedLots}
                  onChange={() => setShowArchivedLots(!showArchivedLots)}
                  className="hidden-checkbox"
                />
                <span
                  className={`checkbox-styler ${showArchivedLots ? 'checked' : ''}`}
                ></span>
                <span className="checkbox-label">Show Archived</span>
              </label>
            </div>
          </div>
          <button className="addlot-button" onClick={() => setShowAddLotModal(true)}>
            Add Lot
          </button>
        </div>

        <table className="dashboard-table">
          <thead>
            <tr>
              {['lotId', 'companyName', 'lotName', 'address'].map((key) => (
                <th
                  key={key}
                  onClick={() => handleSort(key as keyof Lot)}
                  className="sortable-column"
                >
                  <div className="header-content">
                    <span className="header-text">
                      {key === 'companyName'
                        ? 'Company'
                        : key === 'lotName'
                          ? 'Lot Name'
                          : key === 'lotId'
                            ? 'Lot ID'
                            : key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                    <img
                      src={sortConfig.key === key ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                      alt="Sort Arrow"
                      className={`sort-arrow ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                    />
                  </div>
                </th>
              ))}
              <th>
                <span className="header-text">Devices</span>
              </th>
              <th
                onClick={() => handleSort('createdOn')}
                className="sortable-column"
              >
                <div className="header-content">
                  <span className="header-text">Purchased</span>
                  <img
                    src={sortConfig.key === 'createdOn' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                    alt="Sort Arrow"
                    className={`sort-arrow ${sortConfig.key === 'createdOn' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                  />
                </div>
              </th>
              <th>
                <span className="header-text">Operator</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && filteredLots.length === 0 ? (
              <tr className="loading-indicator-row">
                <td colSpan={7} className="pulse-animation">
                  Loading lot data...
                </td>
              </tr>
            ) : filteredLots.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  No lots found.
                </td>
              </tr>
            ) : (
              filteredLots.map((lot, index) => {
                const { online, calibrating, offline, total } = calculateDeviceStatus(lot.lotId);
                const visibleDevices = 8;
                const additionalDevices = total - visibleDevices;
                const dotsToRender = Array(Math.min(visibleDevices, total))
                  .fill(0)
                  .map((_, idx) => {
                    if (idx < online) {
                      return <span key={idx} className="dot green"></span>;
                    } else if (idx < online + calibrating) {
                      return <span key={idx} className="dot blue"></span>;
                    } else {
                      return <span key={idx} className="dot red"></span>;
                    }
                  });

                return (
                  <tr key={index} onClick={() => handleOpenLot(lot.lotId)}>
                    {/* Use our helper to display the formatted Lot ID */}
                    <td>{formatLotId(lot.displayLotId)}</td>
                    <td>{lot.companyName}</td>
                    <td>{lot.lotName}</td>
                    <td>{lot.address}</td>
                    <td>
                      {dotsToRender}
                      {additionalDevices > 0 && <span className="extra-devices">+{additionalDevices}</span>}
                    </td>
                    <td>{formatDate(lot.createdOn)}</td>
                    <td>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Get the current token using the auth utility
                          const token = getToken();
                          if (token) {
                            // Encode the token to make it URL-safe
                            const encodedToken = encodeURIComponent(token);

                            if (process.env.NODE_ENV === "development") {
                              // For debugging only
                              console.log("SuperadminFE - Token being passed:", token.substring(0, 20) + "...");
                            }

                            // Store token in session storage for direct pickup
                            sessionStorage.setItem('superadmin_token_transfer', token);

                            // Open operator portal with the lot ID and token
                            window.open(
                              `https://operator.parkwithparallel.com/lot/${lot.lotId}/dashboard/revenue?superadmin=true&token=${encodedToken}`,
                              "_blank"
                            );
                          } else {
                            alert("Your session has expired. Please log in again.");
                          }
                        }}
                      >
                        🡥 Open
                      </a>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Infinite scroll loading indicator */}
        {hasMore && (
          <div ref={loaderRef} style={{ textAlign: 'center', padding: '20px' }}>
            {isLoading ? (
              <div className="pulse-animation">Loading more lots...</div>
            ) : (
              <div>Scroll for more entries</div>
            )}
          </div>
        )}
      </div>

      {showAddLotModal && (
        <AddLot
          existingLots={lots}
          onClose={() => setShowAddLotModal(false)}
          onLotAdded={handleLotCreated}
        />
      )}
    </div>
  );
};

export default Dashboard;
